import * as React from 'react'
import { useDeferredValue, useState } from 'react'

import { debounce } from 'lodash'

import { useCallback, useUnsafeCallback } from './useCallback'
import { useUnsafeEffect } from './useEffect'
import { useUnsafeMemo } from './useMemo'

export interface Search {
  /**
   * Reactive value, reflects what user has typed. Use for inputs and UI
   */
  search: string
  clearSearch: () => void
  onSearch: (event: React.ChangeEvent<HTMLInputElement>) => void
}

/**
 * @deprecated move to useDebouncedSearch for better performance
 */
export default function useSearch(): Search {
  const [search, setSearch] = React.useState('')

  const onSearch = useCallback((event: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(event.target.value)
  }, [])

  const clearSearch = useCallback(() => {
    setSearch('')
  }, [])

  return {
    search,
    clearSearch,
    onSearch,
  }
}

export type DebouncedSearch = Search & {
  /**
   * Deferred value, use to trigger search action
   */
  debouncedSearch: string
}

/**
 * The chosen value for the delay is 100ms. This lies between the minimum
 * keypress detection time for most keyboards which is around 4ms and the
 * minimum human reaction time which is around 250ms. This ensures that fast
 * typing will not trigger a search request for each keystroke while keeping the
 * UI responsive.
 */
const DEBOUNCE_DELAY_MS = 100

const useDebouncedCallback = <Input, Output>(
  callback: (...args: Input[]) => Output,
  delay: number
) => {
  return useUnsafeMemo(() => debounce(callback, delay), [callback, delay])
}

/**
 * Hook to debounce a search input.
 *
 * This hook provides an easy interface to have a debounced search value. Input
 * fields that trigger server requests should probably have a measure of
 * debouncing to prevent unnecessary requests while the user is still typing.
 *
 * Besides, it's often useful in these cases to limit the minimum number of
 * characters before actually starting to execute a search, to avoid very short
 * intermediate values while the user types.
 *
 * With default values these hook will only defer updating the search value
 * with React updates but two additional parameters can be used to control the
 * debounce behavior. One is the minimum number of characters before the
 * debounce search is triggered, the other is a boolean flag that will impose
 * a minimum delay in milliseconds between two updates of the debounced search
 * value.
 *
 * @param initialValue - the initial value of the search input
 * @param opts - options to control the debounce behavior
 * @param opts.minimumChars - the minimum number of characters before the
 *        debounce search is triggered, < 2 means no minimum, defaults to 0
 * @param opts.delay - flag to enable/disable debouncing, defaults to false
 * @returns An object containing the search value, the debounced search value,
 *        and functions to clear and update the searched value
 */
export const useDebouncedSearch = (
  initialValue = '',
  {
    minimumCharacters: minimumChars = 0,
    delay = false,
  }: { minimumCharacters?: number; delay?: boolean } = {}
): DebouncedSearch => {
  const [search, setSearch] = useState(initialValue)
  const [debounced, setDebounced] = useState(initialValue)
  const debouncedSetter = useDebouncedCallback(setDebounced, DEBOUNCE_DELAY_MS)
  // Cleanup any remaining debounced calls when the component unmounts
  // NOTE: Double arrow is intentional as we want to set a cleanup function
  useUnsafeEffect(() => () => debouncedSetter.cancel(), [debouncedSetter])

  const setter = useUnsafeCallback(
    (value) => {
      setSearch(value)
      delay ? debouncedSetter(value) : setDebounced(value)
    },
    [delay, debouncedSetter]
  )

  const onSearch = useUnsafeCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => setter(event.target.value),
    [setter]
  )
  const clearSearch = useUnsafeCallback(() => setter(''), [setter])
  const debouncedSearch = useDeferredValue(debounced.length < minimumChars ? '' : debounced)

  return { search, debouncedSearch, clearSearch, onSearch }
}
